import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { domainService, DomainAvailability, DomainSuggestion } from '../services/domainService';
import { useAuth } from '../contexts/AuthContext';
import { FaGlobe, FaCheck, FaTimes, FaExchangeAlt } from 'react-icons/fa';

const DomainSearchResults: React.FC = () => {
  const { domainName } = useParams<{ domainName: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [results, setResults] = useState<DomainAvailability[]>([]);
  const [suggestions, setSuggestions] = useState<DomainSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const selectedTlds = ['.com', '.net', '.org', '.io', '.co', '.app', '.dev'];

  useEffect(() => {
    // Scroll to top of page when component mounts
    window.scrollTo(0, 0);

    // Check authentication
    if (!user && !localStorage.getItem('token')) {
      const currentUrl = `/domain-search/${domainName}`;
      const returnUrl = encodeURIComponent(currentUrl);
      navigate(`/login?return=${returnUrl}&message=Please sign in to search for domains`);
      return;
    }

    if (domainName) {
      setSearchTerm(domainName);
      performSearch(domainName);
    } else {
      navigate('/domains');
    }
  }, [domainName, user, navigate]);

  const performSearch = async (searchTermParam: string) => {
    const cleanTerm = searchTermParam.trim().toLowerCase();

    setLoading(true);
    setError('');

    try {
      // Two cases:
      //  1) Full domain like "sivra.click" → check that one domain directly.
      //  2) Bare label like "sivra" → fan out across the default TLD set.
      const isFullDomain = cleanTerm.includes('.');

      if (isFullDomain) {
        console.log('🔍 Checking single domain availability:', cleanTerm);
        const response = await domainService.checkDomainAvailability(cleanTerm);
        if (response.success && response.data) {
          setResults([response.data]);
          // No suggestions on single-domain check — keep the page focused.
          setSuggestions([]);
          console.log('✅ Single domain check successful');
        } else {
          setError(response.error || 'Failed to check domain availability');
          setResults([]);
          setSuggestions([]);
        }
      } else {
        console.log('🔍 Searching domains across TLDs:', cleanTerm, selectedTlds);
        const response = await domainService.searchDomains(cleanTerm, selectedTlds);
        if (response.success && response.data) {
          setResults(response.data.results);
          setSuggestions(response.data.suggestions);
          console.log('✅ Search successful, found results:', response.data.results.length);
        } else {
          setError(response.error || 'Failed to search domains');
          setResults([]);
          setSuggestions([]);
        }
      }
    } catch (error: any) {
      console.error('💥 Search error:', error);
      setError(`Search failed: ${error.message || error}.`);
      setResults([]);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDomainCheckout = (domain: DomainAvailability) => {
    navigate(`/checkout/domain/${encodeURIComponent(domain.domainName)}`, {
      state: { domain }
    });
  };

  const handleTransferDomain = (domainName: string) => {
    navigate('/domain-transfer', { state: { transferDomain: domainName } });
  };

  const handleAlternativeSearch = (altDomain: string) => {
    navigate(`/domain-search/${encodeURIComponent(altDomain)}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-12 shadow-xl relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
              <div className="relative z-10">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 via-purple-500 to-blue-600 rounded-2xl mb-6 shadow-lg relative overflow-hidden mx-auto">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white relative z-10"></div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Checking Domain Availability</h2>
                <p className="text-gray-600 max-w-md mx-auto">
                  Checking domain availability for <span className="font-semibold">{searchTerm}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-12 shadow-xl relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
              <div className="relative z-10">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-500 via-red-600 to-red-700 rounded-2xl mb-6 shadow-lg relative overflow-hidden mx-auto">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                  <FaTimes className="w-10 h-10 text-white relative z-10" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Search Error</h2>
                <p className="text-gray-600 mb-8 max-w-md mx-auto">{error}</p>
                <button
                  onClick={() => navigate('/domains')}
                  className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-lg font-semibold hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
                  <span className="relative z-10">Back to Domain Search</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const availableDomains = results.filter(domain => domain.available);
  const unavailableDomains = results.filter(domain => !domain.available);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-full">
            <span className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full mr-3"></span>
            <span className="text-sm font-medium text-gray-700">Domain Search Results</span>
          </div>
          
          <h1 className="text-4xl font-black text-gray-900 mb-4">
            Results for
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">
              {searchTerm}
            </span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Checked domain availability
          </p>
        </div>

        {/* Available Domains */}
        {availableDomains.length > 0 && (
          <div className="mb-12">
            <div className="bg-gradient-to-br from-green-50 via-white to-green-50 border border-green-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
              <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl mr-4 shadow-lg relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-xl"></div>
                    <FaCheck className="w-6 h-6 text-white relative z-10" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Available Domains</h2>
                </div>
                
                <div className="grid gap-4">
                  {availableDomains.map((domain, index) => (
                    <div
                      key={index}
                      className="bg-white border border-green-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer group"
                      onClick={() => handleDomainCheckout(domain)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-green-500 rounded-full mr-4"></div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 group-hover:text-green-600">
                              {domain.domainName}
                            </h3>
                            <p className="text-gray-600">Ready for registration</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-600 mb-2">
                            ${domain.price?.toFixed(2)}/year
                          </div>
                          <button
                            onClick={() => handleDomainCheckout(domain)}
                            className="bg-gradient-to-br from-green-500 to-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-lg relative overflow-hidden group"
                          >
                            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
                            <span className="relative z-10">Register Now</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Unavailable Domains with Transfer Option */}
        {unavailableDomains.length > 0 && (
          <div className="mb-12">
            <div className="bg-gradient-to-br from-orange-50 via-white to-yellow-50 border border-orange-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
              <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl mr-4 shadow-lg relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-xl"></div>
                    <FaExchangeAlt className="w-6 h-6 text-white relative z-10" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Transfer Existing Domains</h2>
                </div>
                
                <div className="grid gap-4">
                  {unavailableDomains.map((domain, index) => (
                    <div
                      key={index}
                      className="bg-white border border-orange-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-orange-500 rounded-full mr-4"></div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">
                              {domain.domainName}
                            </h3>
                            <p className="text-gray-600">Already registered - transfer to 12and22</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600 mb-2">Free transfer + 1 year extension</div>
                          <button
                            onClick={() => handleTransferDomain(domain.domainName)}
                            className="bg-gradient-to-br from-orange-500 to-yellow-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-yellow-600 transition-all duration-200 shadow-lg relative overflow-hidden group"
                          >
                            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
                            <span className="relative z-10">Transfer Domain</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Alternative TLD Options */}
        <div className="mb-12">
          <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 border border-blue-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
            <div className="relative z-10">
              <div className="flex items-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl mr-4 shadow-lg relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-xl"></div>
                  <FaGlobe className="w-6 h-6 text-white relative z-10" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Try Alternative Extensions</h2>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['.net', '.org', '.io', '.co', '.app', '.dev', '.tech', '.store'].map((tld) => {
                  const baseName = searchTerm.replace(/\.(com|net|org|io|co|app|dev|info|biz)$/i, '');
                  const altDomain = baseName + tld;
                  return (
                    <div
                      key={tld}
                      className="bg-white border border-blue-200 rounded-xl p-4 text-center hover:shadow-lg cursor-pointer transition-all duration-200 group hover:border-blue-400"
                      onClick={() => handleAlternativeSearch(altDomain)}
                    >
                      <div className="font-bold text-lg text-gray-900 group-hover:text-blue-600 mb-2">
                        {altDomain}
                      </div>
                      <div className="text-sm text-blue-600 group-hover:text-blue-700">
                        Click to check availability
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Suggested Name Alternatives */}
        {suggestions.length > 0 && (
          <div className="mb-12">
            <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
              <div className="relative z-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Suggested Name Alternatives</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg cursor-pointer transition-all duration-200 group"
                      onClick={() => handleDomainCheckout({
                        domainName: suggestion.domainName,
                        available: suggestion.available,
                        price: suggestion.price,
                      })}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900 group-hover:text-blue-600">
                            {suggestion.domainName}
                          </h3>
                          <p className="text-sm text-gray-600">Available for registration</p>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-blue-600 mb-2">
                            ${suggestion.price.toFixed(2)}/year
                          </div>
                          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors duration-200">
                            Register
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Back to Search */}
        <div className="text-center">
          <button
            onClick={() => navigate('/domains')}
            className="bg-gradient-to-br from-gray-600 to-gray-700 text-white px-8 py-3 rounded-lg font-semibold hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow-lg relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
            <span className="relative z-10">Search Another Domain</span>
          </button>
        </div>

      </div>
    </div>
  );
};

export default DomainSearchResults;